import {  Box, CircularProgress, Modal, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DOMPurify from "dompurify";
import { Info, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AgentChatBody,
  FooterContainer,
  // Divider,
  LoadingHistoryContainer,
  LoadingHistoryMessage,
  MessagesContainer, ModalContainer,
  ModalHeaderContainer, RootContainer, SessionTerminatedMessage, SessionTerminatedMessageContainer,
  // TitleContainer,
  // TitleContainer,
  WelcomeMessage, WelcomeMessageContainer } from "./AISupportChat.styles";
import { FileContext } from "./components/AIFileContext/FileContext.component";
import AISupportChatErrorOverlay from "./components/AISupportChatErrorOverlay/AISupportChatErrorOverlay.component";
import AISupportChatMessageBox from "./components/AISupportChatMessageBox/AISupportChatMessageBox.component";
import AISupportChatMessageInput from "./components/AISupportChatMessageInput/AISupportChatMessageInput";
import ChatAgentSidebar from "./components/HistoryChatSidebar/ChatAgentSidebar.component";
import { useApiAgentChatAxios } from "./useApiAgentChatAxios.hook";
import { handleSelectConversation } from "./utils";
import { useAISupportChat } from "../../context/AISupportChatContext";


const AISupportChat = () => {

  const { t } = useTranslation();
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { 
    isOpen, 
    isPrelogin,
    closeChat: onClose, 
    sessionId,
    conversationsList,
    createNewConversation,
    isCreatingConversation,
    isLoadingConversations,
    selectConversation,
    loadConversationsList,
    deleteConversation,
    freshSession,
    updateConversationMeta
  } = useAISupportChat();
  
  const {
    apiError,
    sendMessage,
    isSendingMessage,
    initChat,
    messages,
    blockUserInput,
    messagesContainerRef,
    messageInputRef,
    getChatHistory,
    isRetrievingHistory,
    uploadImageContext,
    isUploadingImage,
    isDeletingImage,
    contextImgError,
    deleteImageContext,
    getImageContext,
    isRetrievingImageContext,
    listImgContext,
    submitFeedback
  } = useApiAgentChatAxios(sessionId, updateConversationMeta, loadConversationsList);

  const [showLegalDisclaimer, setShowLegalDisclaimer] = useState<boolean>(true);

  const handleNewChat = async () => {
    if (
      blockUserInput ||
      isSendingMessage ||
      isRetrievingHistory ||
      isCreatingConversation
    )
      return;
    // if (isMobile) setMobileSidebarOpen(false);
    createNewConversation();
    initChat();
  };

  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.sender === "assistant" && m.response !== "") return i;
    }
    return -1;
  }, [messages]);

  useEffect(() => {
    initChat();
    if (isOpen) loadConversationsList();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    
    if (sessionId) {
      getChatHistory(sessionId);
      getImageContext(sessionId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId]);

  const isSessionTerminated = messages.find((message) => message.end_session);

  return (
    <Modal
      open={ isOpen }
      onClose={ onClose }
      sx={ {
        zIndex: 1401
      } }
      slotProps={ {
        backdrop: {
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.05)"
          }
        }
      } }
    >
      <RootContainer id="root-container">
        <ModalContainer theme={ theme }>
          <ChatAgentSidebar
            isPrelogin={ isPrelogin }
            conversationsList={ conversationsList }
            isLoading={ isLoadingConversations }
            activeSessionId={ sessionId }
            onSelect={ (sid) => handleSelectConversation(
              sid,
              sessionId,
              blockUserInput,
              isSendingMessage,
              isCreatingConversation,
              selectConversation
              // isMobile, 
              // setMobileSidebarOpen,
            ) }
            onNewChat={ handleNewChat }
            onDelete={ deleteConversation }
            disabled={
              blockUserInput || isSendingMessage || isCreatingConversation || freshSession.isFresh
            }
            historyEnabled={ true }
          />
          <AgentChatBody 
            id="agent-chat-body"
          >
            <ModalHeaderContainer>
              { /* <TitleContainer gap={ 2 }>
                <Box
                  sx={ {
                    alignItems: "center",
                    backgroundColor: theme.customColors.systemSecondary01,
                    borderRadius: "4px",
                    color: theme.customColors.textPrimaryCta,
                    display: "flex",
                    flexShrink: 0,
                    fontSize: "14px",
                    fontWeight: 700,
                    height: "40px",
                    justifyContent: "center",
                    width: "40px"
                  } }
                >
                  AI
                </Box>
                <Box sx={ { display: "flex", flexDirection: "column" } }>
                  <Typography
                    variant="h4"
                    component={ "h2" }
                    fontWeight={ 700 }
                    fontSize={ "1.125rem" }
                  >
                    { t("ai_support_chat_title") }
                  </Typography>
                  <Typography sx={ { color: theme.customColors.textDisabledAlternative, fontSize: "12px" } }>
                    { t("ai_support_chat_subtitle") }
                  </Typography>
                </Box>
              </TitleContainer> */ }
              <Box
                tabIndex={ -1 }
                id={ "badge-agent-disclaimer" } 
                data-testid="badge-agent-disclaimer"
                aria-label={ t("badge_aria_label") }
                sx={ {
                  display: showLegalDisclaimer ? "flex" : "none",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0.75rem 1rem",
                  gap: "0.75rem",
                  border: `0.0625rem solid ${theme.customColors.backgroundAccent}`,
                  backgroundColor: theme.customColors.backgroundSecondaryAlternative,
                  borderRadius: "0.25rem"
                } }
              >
                <Box
                  sx={ {
                    width: "2rem",
                    display: "flex",
                    alignItems: "center"
                  } }
                >
                  <Info 
                    size={ "1.5rem" } 
                    color={ theme.customColors.backgroundAccent }
                  />
                </Box>
                <Typography 
                  tabIndex={ -1 } 
                  sx={ { 
                    fontSize: "0.875rem", 
                    fontWeight: "400"
                  } }
                  color={ theme.customColors.textTag }
                >
                  { t("ai_support_legal_disclaimer") }
                </Typography>
                <Box
                  sx={ {
                    width: "1.5rem",
                    display: "flex",
                    alignItems: "center"
                  } }
                >
                  <X 
                    id={ "closelegal-disclaimer" }
                    aria-label={ t("close") || "close" }
                    onClick={ () => setShowLegalDisclaimer(false) }
                    style={ {
                      cursor: "pointer"
                    } }
                    size={ "1.5rem" }  
                    color={ theme.customColors.textTag }
                  />
                </Box>
              </Box>
            </ModalHeaderContainer>

            <X 
              id={ "close" }
              aria-label={ t("close") || "close" }
              onClick={ ()=> !isSendingMessage && onClose() }
              style={ {
                position: "absolute",
                top: "1rem",
                right: "0.75rem",
                paddingRight: 0,
                cursor: "pointer"
              } }
              size={ "1.5rem" }  
              color={ theme.customColors.textDisabledAlternative }
            />

            <MessagesContainer
              theme={ theme }
              blockUserInput={ blockUserInput }
              ref={ messagesContainerRef }
              gap={ "20px" }
            >
              { messages && messages.length > 0 ? (
                <>
                  { messages.map((message, index) => (
                    <AISupportChatMessageBox
                      key={ `chat_message_${index}` }
                      message={ message }
                      messagesList={ messages }
                      submitFeedback={ submitFeedback }
                      index={ index }
                      onSuggestionClick={
                        blockUserInput || isSendingMessage
                          ? undefined
                          : (question) => sendMessage(question)
                      }
                      showRateExperience={ index === lastAssistantIndex }
                    />
                  )) }
                </>
              ) : (
                <WelcomeMessageContainer gap={ "20px" }>
                  { /*<AutoAwesome sx={ { width: "60px", height: "60px" } } />*/ }
                  <WelcomeMessage
                    dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize(t("ai_support_chat_message") || "") } }
                  />
                  { isRetrievingHistory && (
                    <LoadingHistoryContainer>
                      <CircularProgress size={ 20 } />
                      <LoadingHistoryMessage>
                        { t("ai_support_chat_retrieving_history") }
                      </LoadingHistoryMessage>
                    </LoadingHistoryContainer>
                  ) } 
                </WelcomeMessageContainer>
              ) }
            </MessagesContainer>

            { apiError && (
              <AISupportChatErrorOverlay
                error={ apiError }
                onReset={ initChat }
              />
            ) }

            { /* <Divider /> */ }
            <FooterContainer>
              { (isSessionTerminated || apiError) ? (
                <SessionTerminatedMessageContainer>
                  <SessionTerminatedMessage>
                    { t("ai_support_chat_session_terminated") }
                  </SessionTerminatedMessage>
                </SessionTerminatedMessageContainer>
              ) : (
                <>
                  <FileContext
                    listImgContext={ listImgContext }
                    onDeleteImage={ deleteImageContext }
                    isLoading={ isRetrievingImageContext }
                  />
                  <AISupportChatMessageInput
                    isLoading={ isSendingMessage }
                    onSendMessage={ (message) => sendMessage(message) }
                    onUploadImage={ uploadImageContext }
                    isHandlingImage={ isUploadingImage || isDeletingImage }
                    disabled={ blockUserInput || !!apiError }
                    inputRef={ messageInputRef }
                    isPrelogin={ isPrelogin }
                    listImgContext={ listImgContext }
                  />
                </>
              ) }
              { contextImgError && (
                <Typography
                  sx={ {
                    color: theme.customColors.textError,
                    fontSize: "11px",
                    marginTop: "4px",
                    width: "100%"
                  } }
                >
                  { contextImgError }
                </Typography>
              ) }
              <Typography
                sx={ {
                  color: theme.customColors.textDisabledAlternative,
                  fontSize: "11px",
                  marginTop: "10px",
                  width: "100%"
                } }
              >
                { t("ai_support_chat_disclaimer") }
              </Typography>

            </FooterContainer>

          </AgentChatBody>
        </ModalContainer>
      </RootContainer>
    </Modal>
  );
};

export default AISupportChat;
